import * as io from "io-ts";

export const MetadataResolver = io.type({
    language: io.string,
    transaction_id: io.string,
    version: io.string,
    latitude: io.number,
    longitude: io.number,
    units: io.string,
    expire_time_gmt: io.number,
    status_code: io.number,
});
export type WeatherMetadata = io.TypeOf<typeof MetadataResolver>;

export const ImperialResolver = io.intersection([
    io.type({
        temp: io.number,
        rh: io.number,
        altimeter: io.number,
        wspd: io.number,
        vis: io.number,
    }),
    io.partial({
        gust: io.union([io.number, io.null]),
        mslp: io.number,
        dewpt: io.number,
        wc: io.number,
        hi: io.number,
        temp_change_24hour: io.number,
        temp_max_24hour: io.number,
        temp_min_24hour: io.number,
        pchange: io.number,
        feels_like: io.number,
        snow_1hour: io.number,
        snow_6hour: io.number,
        snow_24hour: io.number,
        snow_mtd: io.number,
        snow_season: io.number,
        snow_ytd: io.number,
        snow_2day: io.number,
        snow_3day: io.number,
        snow_7day: io.number,
        ceiling: io.union([io.number, io.null]),
        precip_1hour: io.number,
        precip_6hour: io.number,
        precip_24hour: io.number,
        precip_mtd: io.number,
        precip_ytd: io.number,
        precip_2day: io.number,
        precip_3day: io.number,
        precip_7day: io.number,
        obs_qualifier_100char: io.union([io.string, io.null]),
        obs_qualifier_50char: io.union([io.string, io.null]),
        obs_qualifier_32char: io.union([io.string, io.null]),
    }),
]);

export const ObservationResolver = io.intersection([
    io.type({
        icon_code: io.number,
        imperial: ImperialResolver,
    }),
    io.partial({
        class: io.union([io.string, io.null]),
        expire_time_gmt: io.number,
        obs_time: io.number,
        obs_time_local: io.union([io.string, io.null]),
        wdir: io.number,
        icon_extd: io.number,
        sunrise: io.string,
        sunset: io.string,
        day_ind: io.union([io.string, io.null]),
        uv_index: io.number,
        uv_warning: io.number,
        wxman: io.union([io.string, io.null]),
        obs_qualifier_code: io.union([io.string, io.null]),
        ptend_code: io.number,
        dow: io.union([io.string, io.null]),
        wdir_cardinal: io.union([io.string, io.null]),
        uv_desc: io.union([io.string, io.null]),
        phrase_12char: io.union([io.string, io.null]),
        phrase_22char: io.union([io.string, io.null]),
        phrase_32char: io.union([io.string, io.null]),
        ptend_desc: io.union([io.string, io.null]),
        sky_cover: io.union([io.string, io.null]),
        clds: io.union([io.string, io.null]),
        obs_qualifier_severity: io.union([io.number, io.null]),
        vocal_key: io.union([io.string, io.null]),
    }),
]);
export type Observation = io.TypeOf<typeof ObservationResolver>;

export const WeatherCurrentResolver = io.type({
    metadata: MetadataResolver,
    observation: ObservationResolver,
});

export type WeatherCurrent = io.TypeOf<typeof WeatherCurrentResolver>;

// Forecast Types
export const DayResolver = io.partial({
    fcst_valid: io.number,
    fcst_valid_local: io.string,
    day_ind: io.string,
    thunder_enum: io.number,
    daypart_name: io.string,
    long_daypart_name: io.string,
    alt_daypart_name: io.string,
    thunder_enum_phrase: io.string,
    num: io.number,
    temp: io.number,
    hi: io.number,
    wc: io.number,
    pop: io.number,
    icon_extd: io.number,
    icon_code: io.number,
    wxman: io.string,
    phrase_12char: io.string,
    phrase_22char: io.string,
    phrase_32char: io.string,
    subphrase_pt1: io.union([io.string, io.null]),
    subphrase_pt2: io.union([io.string, io.null]),
    subphrase_pt3: io.union([io.string, io.null]),
    precip_type: io.string,
    rh: io.number,
    wspd: io.number,
    wdir: io.number,
    wdir_cardinal: io.string,
    clds: io.number,
    pop_phrase: io.union([io.string, io.null]),
    temp_phrase: io.string,
    accumulation_phrase: io.union([io.string, io.null]),
    wind_phrase: io.string,
    shortcast: io.string,
    narrative: io.string,
    qpf: io.number,
    snow_qpf: io.number,
    snow_range: io.union([io.string, io.null]),
    snow_phrase: io.union([io.string, io.null]),
    snow_code: io.union([io.string, io.null]),
    vocal_key: io.string,
    qualifier_code: io.union([io.string, io.null]),
    qualifier: io.union([io.string, io.null]),
    uv_index_raw: io.number,
    uv_index: io.number,
    uv_warning: io.number,
    uv_desc: io.string,
    golf_index: io.number,
    golf_category: io.string,
});
export type DayForecast = io.TypeOf<typeof DayResolver>;

export const NightResolver = io.partial({
    fcst_valid: io.number,
    fcst_valid_local: io.string,
    day_ind: io.string,
    thunder_enum: io.number,
    daypart_name: io.string,
    long_daypart_name: io.string,
    alt_daypart_name: io.string,
    thunder_enum_phrase: io.string,
    num: io.number,
    temp: io.number,
    hi: io.number,
    wc: io.number,
    pop: io.number,
    icon_extd: io.number,
    icon_code: io.number,
    wxman: io.string,
    phrase_12char: io.string,
    phrase_22char: io.string,
    phrase_32char: io.string,
    subphrase_pt1: io.union([io.string, io.null]),
    subphrase_pt2: io.union([io.string, io.null]),
    subphrase_pt3: io.union([io.string, io.null]),
    precip_type: io.string,
    rh: io.number,
    wspd: io.number,
    wdir: io.number,
    wdir_cardinal: io.string,
    clds: io.number,
    pop_phrase: io.union([io.string, io.null]),
    temp_phrase: io.string,
    accumulation_phrase: io.union([io.string, io.null]),
    wind_phrase: io.string,
    shortcast: io.string,
    narrative: io.string,
    qpf: io.number,
    snow_qpf: io.number,
    snow_range: io.union([io.string, io.null]),
    snow_phrase: io.union([io.string, io.null]),
    snow_code: io.union([io.string, io.null]),
    vocal_key: io.string,
    qualifier_code: io.union([io.string, io.null]),
    qualifier: io.union([io.string, io.null]),
    uv_index_raw: io.number,
    uv_index: io.number,
    uv_warning: io.number,
    uv_desc: io.string,
    golf_index: io.union([io.number, io.null]),
    golf_category: io.union([io.string, io.null]),
});
export type NightForecast = io.TypeOf<typeof NightResolver>;

export const ForecastResolver = io.partial({
    night: NightResolver,
    day: DayResolver,
    class: io.string,
    expire_time_gmt: io.number,
    fcst_valid: io.number,
    fcst_valid_local: io.string,
    num: io.number,
    max_temp: io.union([io.number, io.null]),
    min_temp: io.number,
    torcon: io.union([io.string, io.null]),
    stormcon: io.union([io.string, io.null]),
    blurb: io.union([io.string, io.null]),
    blurb_author: io.union([io.string, io.null]),
    lunar_phase_day: io.number,
    dow: io.string,
    lunar_phase: io.string,
    lunar_phase_code: io.string,
    sunrise: io.string,
    sunset: io.string,
    moonrise: io.string,
    moonset: io.string,
    qualifier_code: io.union([io.string, io.null]),
    qualifier: io.union([io.string, io.null]),
    narrative: io.string,
    qpf: io.number,
    snow_qpf: io.number,
    snow_range: io.union([io.string, io.null]),
    snow_phrase: io.union([io.string, io.null]),
    snow_code: io.union([io.string, io.null]),
});

export type Forecast = io.TypeOf<typeof ForecastResolver>;

export const DaysForecastResolver = io.array(ForecastResolver);
export type DaysForecast = io.TypeOf<typeof DaysForecastResolver>;

export const ForecastListResolver = io.type({
    metadata: MetadataResolver,
    forecasts: DaysForecastResolver,
});

export type ForecastList = io.TypeOf<typeof ForecastListResolver>;
